/**
 * IndexedDB for the print file review page. Everything a designer needs to resume a
 * review lives here: batches, files (decision + sync state), downloaded/edited images,
 * the edit log and the outbox of pending server writes.
 */

const DB_NAME = 'print-file-review';
const DB_VERSION = 1;

export type StoreName = 'batches' | 'files' | 'blobs' | 'logs' | 'outbox';

let dbPromise: Promise<IDBDatabase> | null = null;

export const openReviewDb = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB is not available in this browser'));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains('batches')) db.createObjectStore('batches', { keyPath: 'id' });
            if (!db.objectStoreNames.contains('files')) {
                db.createObjectStore('files', { keyPath: 'id' }).createIndex('batchId', 'batchId');
            }
            if (!db.objectStoreNames.contains('blobs')) {
                const blobs = db.createObjectStore('blobs', { keyPath: 'key' });
                blobs.createIndex('batchId', 'batchId');
                blobs.createIndex('fileId', 'fileId');
            }
            if (!db.objectStoreNames.contains('logs')) {
                const logs = db.createObjectStore('logs', { keyPath: 'id', autoIncrement: true });
                logs.createIndex('batchId', 'batchId');
                logs.createIndex('fileId', 'fileId');
            }
            if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox', { keyPath: 'key' });
        };
        req.onsuccess = () => {
            const db = req.result;
            db.onversionchange = () => {
                db.close();
                dbPromise = null;
            };
            resolve(db);
        };
        req.onerror = () => {
            dbPromise = null;
            reject(req.error);
        };
        req.onblocked = () => console.warn('print-file-review DB upgrade is blocked by another open tab');
    });
    return dbPromise;
};

const promisify = <T,>(r: IDBRequest<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error);
    });

/**
 * Run `fn` in one transaction and resolve after it commits. Only await IDB requests
 * inside `fn` — awaiting anything else lets the transaction auto-commit early.
 */
export const withStores = async <T,>(
    names: StoreName | StoreName[],
    mode: IDBTransactionMode,
    fn: (t: IDBTransaction) => Promise<T> | T
): Promise<T> => {
    const db = await openReviewDb();
    const t = db.transaction(names, mode);
    const committed = new Promise<void>((resolve, reject) => {
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error ?? new Error('Transaction aborted'));
    });
    let result: T;
    try {
        result = await fn(t);
    } catch (e) {
        committed.catch(() => undefined);
        try {
            t.abort();
        } catch (abortError) {
            /* already finished */
        }
        throw e;
    }
    await committed;
    return result;
};

export const dbGet = <T,>(store: StoreName, key: IDBValidKey) =>
    withStores(store, 'readonly', (t) => promisify(t.objectStore(store).get(key)) as Promise<T | undefined>);

export const dbGetAll = <T,>(store: StoreName) =>
    withStores(store, 'readonly', (t) => promisify(t.objectStore(store).getAll()) as Promise<T[]>);

export const dbGetAllByIndex = <T,>(store: StoreName, index: string, key: IDBValidKey) =>
    withStores(store, 'readonly', (t) => promisify(t.objectStore(store).index(index).getAll(key)) as Promise<T[]>);

export const dbPut = <T,>(store: StoreName, value: T) =>
    withStores(store, 'readwrite', (t) => promisify(t.objectStore(store).put(value)));

export const dbPutMany = (entries: { store: StoreName; value: unknown }[]) => {
    const names = Array.from(new Set(entries.map((e) => e.store)));
    return withStores(names, 'readwrite', async (t) => {
        for (const e of entries) await promisify(t.objectStore(e.store).put(e.value));
    });
};

export const dbDelete = (store: StoreName, key: IDBValidKey) =>
    withStores(store, 'readwrite', (t) => promisify(t.objectStore(store).delete(key)));

/** Delete every record of `store` whose `index` equals `key`. Returns how many were deleted. */
export const dbDeleteByIndex = (store: StoreName, index: string, key: IDBValidKey, keep?: (value: any) => boolean) =>
    withStores(store, 'readwrite', (t) =>
        new Promise<number>((resolve, reject) => {
            let count = 0;
            const req = t.objectStore(store).index(index).openCursor(IDBKeyRange.only(key));
            req.onsuccess = () => {
                const cursor = req.result;
                if (!cursor) {
                    resolve(count);
                    return;
                }
                if (!keep || !keep(cursor.value)) {
                    cursor.delete();
                    count += 1;
                }
                cursor.continue();
            };
            req.onerror = () => reject(req.error);
        })
    );

/**
 * Atomic read-modify-write of one record. The updater returns the new value, `null` to
 * delete the record, or `undefined` to leave it untouched.
 * Resolves with the value that is stored afterwards (`null` when deleted/absent).
 */
export const dbUpdate = <T,>(store: StoreName, key: IDBValidKey, updater: (current: T | undefined) => T | null | undefined) =>
    withStores(store, 'readwrite', async (t) => {
        const os = t.objectStore(store);
        const current = (await promisify(os.get(key))) as T | undefined;
        const next = updater(current);
        if (next === undefined) return { changed: false, value: current ?? null };
        if (next === null) {
            await promisify(os.delete(key));
            return { changed: true, value: null };
        }
        await promisify(os.put(next));
        return { changed: true, value: next as T };
    });
